import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Phone, Zap, CheckCircle2, ArrowRight, Play, Share2, MessageSquare, PhoneCall, Rocket, Calendar } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SocialShareButtons from "@/components/SocialShareButtons";
import RotatingNewsBanner from "@/components/RotatingNewsBanner";
import { Helmet } from "react-helmet";
import logo from "@/assets/logo.jpg";
import ChatInterface from "@/components/onboarding/ChatInterface";
import TestOptions from "@/components/onboarding/TestOptions";
import RegistrationPrompt from "@/components/onboarding/RegistrationPrompt";
import { ConversionTracking, trackConversion } from "@/components/tracking/ConversionTracking";
import { CookieConsent } from "@/components/tracking/CookieConsent";
import { LanguageSelector } from "@/components/LanguageSelector";
import { CalendlyModal } from "@/components/CalendlyModal";
type FlowStep = 'chat' | 'test' | 'register';
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}
const AdLanding = () => {
  const {
    t,
    i18n
  } = useTranslation();
  const [flowStep, setFlowStep] = useState<FlowStep>('chat');
  const [agentConfig, setAgentConfig] = useState<any>(null);
  const [sessionId, setSessionId] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [quickPromptMessage, setQuickPromptMessage] = useState<string>('');
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';
  useEffect(() => {
    if (flowStep === 'test') {
      trackConversion('AgentCreated', {
        event_category: 'Agent Creation',
        event_label: 'Ad Landing',
        value: 1
      });
    } else if (flowStep === 'register') {
      trackConversion('ReachedSignup', {
        event_category: 'Funnel',
        event_label: 'Ad Landing',
        value: 2
      });
    }
  }, [flowStep]);
  const quickPrompts = [t('adLanding.prompts.bookAppointments'), t('adLanding.prompts.customerSupport'), t('adLanding.prompts.leadQualification'), t('adLanding.prompts.afterHours')];
  return <>
      <CookieConsent />
      <ConversionTracking pageName="Ad Landing Page" />
      <Helmet>
        <title>Start Free AI Customer Support | AI Answering Service | Call24x7</title>
        <meta name="description" content="Get started with AI customer support platform free. 24/7 AI answering service, AI call center software. Automate customer calls with AI in 60 seconds!" />
        <meta name="keywords" content="AI customer support platform, 24x7 AI answering service, AI call center software, AI receptionist service, AI customer service automation, how to automate customer calls with AI, AI tools for small business call handling" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://call24x7.ai/get-started" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://call24x7.ai/get-started" />
        <meta property="og:title" content="Start Free AI Customer Support | AI Answering Service" />
        <meta property="og:description" content="Get started with AI customer support platform free. 24/7 AI answering service. Automate customer calls with AI." />
        <meta property="og:site_name" content="Call24x7.AI" />
        
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Call24x7 AI Customer Support Platform",
          "applicationCategory": "BusinessApplication",
          "description": "AI customer support platform with 24/7 AI answering service. AI call center software for customer service automation.",
          "operatingSystem": "Web",
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "0",
            "highPrice": "499",
            "priceCurrency": "USD",
            "offerCount": "3"
          },
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "ratingCount": "500",
            "bestRating": "5"
          }
        })}
        </script>
        
        {/* Breadcrumb */}
        <script type="application/ld+json">
          {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [{
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://call24x7.ai"
          }, {
            "@type": "ListItem",
            "position": 2,
            "name": "Get Started",
            "item": "https://call24x7.ai/get-started"
          }]
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5" dir={isRTL ? 'rtl' : 'ltr'}>
        <RotatingNewsBanner />
        {/* Minimal Header */}
        <header className="fixed top-10 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
          <div className="w-full px-3 md:px-4 md:py-3 py-[25px]">
            <div className="flex items-center justify-between">
              <a href="/" aria-label="Call24x7 Home" className={`flex items-center gap-1.5 md:gap-2 hover:opacity-80 transition-opacity ${isRTL ? 'flex-row-reverse' : ''}`}>
                <img src={logo} alt="Call24x7 Logo" className="h-7 w-7 md:h-8 md:w-8" width={32} height={32} fetchPriority="high" loading="eager" />
                <span className="text-lg md:text-xl font-bold text-primary">Call24x7</span>
              </a>
              <div className={`flex items-center gap-1.5 md:gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <LanguageSelector />
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs md:text-sm px-2 md:px-3 gap-1"
                  onClick={() => setCalendlyOpen(true)}
                >
                  <Calendar className="h-3 w-3 md:h-4 md:w-4" />
                  <span className="hidden sm:inline">{t('header.bookMeeting')}</span>
                </Button>
                <Button variant="ghost" size="sm" className="text-xs md:text-sm px-2 md:px-3" onClick={() => {
                trackConversion('ClickedLogin', {
                  event_category: 'Navigation',
                  event_label: 'Ad Landing'
                });
                window.location.href = "https://manage.call24x7.ai/login";
              }}>
                  {t('adLanding.header.login')}
                </Button>
                <Button size="sm" className="text-xs md:text-sm px-2 md:px-3" onClick={() => {
                trackConversion('ClickedSignup', {
                  event_category: 'Navigation',
                  event_label: 'Ad Landing'
                });
                window.location.href = "https://manage.call24x7.ai/signup";
              }}>
                  {t('adLanding.header.signUp')}
                </Button>
              </div>
              <CalendlyModal open={calendlyOpen} onOpenChange={setCalendlyOpen} />
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main>
        {/* Hero - Ultra Minimal */}
        <section className="pt-16 md:pt-20 pb-2 px-2 md:px-4 py-0 my-0">
          <div className="w-full max-w-4xl mx-auto text-center my-[17px] py-0 pt-[50px]">
            <Badge className={`mb-3 md:mb-4 bg-primary/10 text-primary border-primary/20 animate-pulse text-xs md:text-sm cursor-pointer hover:bg-primary/20 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`} onClick={() => {
              trackConversion('ClickedBadgeSignup', {
                event_category: 'Navigation',
                event_label: 'Ad Landing'
              });
              window.location.href = "https://manage.call24x7.ai/signup";
            }}>
              <Zap className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} />
              {t('adLanding.hero.badge')}
            </Badge>
            
            <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-4 leading-tight px-1">
              <span className="bg-gradient-to-r from-primary via-primary to-secondary bg-clip-text text-transparent">
                {t('adLanding.hero.title')}
              </span>
              <br />
              <span className="text-foreground text-xl sm:text-2xl md:text-4xl lg:text-5xl">
                {t('adLanding.hero.subtitle')}
              </span>
            </h1>
            
            <p className="text-base md:text-xl text-muted-foreground mb-4 md:mb-6 max-w-2xl mx-auto px-2">
              {t('adLanding.hero.description')}
            </p>

            {/* 3 Simple Steps - Visual */}
            <div className="gap-3 md:gap-4 mb-4 md:mb-8 flex items-center justify-center">
              <Popover>
                <PopoverTrigger asChild>
                  <div className={`flex items-center gap-1.5 md:gap-2 cursor-pointer hover:scale-105 transition-transform ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs md:text-sm font-bold">1</div>
                    <span className="text-xs md:text-base font-medium">{t('adLanding.steps.build')}</span>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" side="bottom">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-primary" />
                      <h4 className="font-semibold">Build Your AI Agent</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Simply chat with us about your business needs. Tell us what you want your AI phone agent to do - handle appointments, answer FAQs, qualify leads, or provide support. We'll create a custom agent tailored to your requirements in minutes.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
              
              <ArrowRight className={`w-3 h-3 md:w-4 md:h-4 text-muted-foreground ${isRTL ? 'rotate-180' : ''}`} />
              
              <Popover>
                <PopoverTrigger asChild>
                  <div className={`flex items-center gap-1.5 md:gap-2 cursor-pointer hover:scale-105 transition-transform ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs md:text-sm font-bold">2</div>
                    <span className="text-xs md:text-base font-medium">{t('adLanding.steps.test')}</span>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" side="bottom">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <PhoneCall className="w-5 h-5 text-primary" />
                      <h4 className="font-semibold">Test Your Agent</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Before going live, make a real test call to your AI agent. Experience exactly how it sounds, responds, and handles conversations. Tweak and refine until it's perfect for your business.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
              
              <ArrowRight className={`w-3 h-3 md:w-4 md:h-4 text-muted-foreground ${isRTL ? 'rotate-180' : ''}`} />
              
              <Popover>
                <PopoverTrigger asChild>
                  <div className={`flex items-center gap-1.5 md:gap-2 cursor-pointer hover:scale-105 transition-transform ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs md:text-sm font-bold">3</div>
                    <span className="text-xs md:text-base font-medium">{t('adLanding.steps.goLive')}</span>
                  </div>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-4" side="bottom">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Rocket className="w-5 h-5 text-primary" />
                      <h4 className="font-semibold">Go Live & Scale</h4>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Sign up and get <strong>60 free minutes</strong> of AI calls! Connect your phone numbers for inbound calls, upload contact lists for outbound campaigns, clone your voice for personalization, create multiple agents, and much more.
                    </p>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </section>

        {/* Main Flow */}
        <section className="px-2 md:px-4 pb-6 md:pb-8 chat-interface">
          <div className="w-full max-w-4xl mx-auto">
            {flowStep === 'chat' && <div className="space-y-3 md:space-y-4">
                {/* Chat Interface */}
                <ChatInterface initialMessages={chatMessages} externalMessage={quickPromptMessage} onExternalMessageSent={() => setQuickPromptMessage('')} onComplete={(config, messages, sid, aid) => {
                setAgentConfig(config);
                setChatMessages(messages);
                setSessionId(sid);
                setAgentId(aid);
                setFlowStep('test');
              }} onBack={() => window.location.href = '/'} />
                
                {/* Quick Start Prompts */}
                <Card className="p-3 md:p-4 bg-card/50 border-border/50">
                  <p className="text-xs md:text-sm text-muted-foreground mb-2 md:mb-3 text-center">
                    {t('adLanding.prompts.hint')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-1.5 md:gap-2">
                    {quickPrompts.map((prompt, i) => <Badge key={i} variant="outline" className="cursor-pointer hover:bg-primary/10 hover:border-primary transition-colors py-1 md:py-1.5 px-2 md:px-3 text-xs md:text-sm" onClick={() => setQuickPromptMessage(prompt)}>
                        {prompt}
                      </Badge>)}
                  </div>
                </Card>
              </div>}

            {flowStep === 'test' && agentConfig && <div className="space-y-4 md:space-y-6 animate-fade-in">
                <Card className="p-4 md:p-6 bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/30">
                  <div className={`flex items-start gap-3 md:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-primary" />
                    </div>
                    <div className={isRTL ? 'text-right' : ''}>
                      <h3 className="text-lg md:text-xl font-bold mb-1">
                        {t('adLanding.test.meet')} <span className="text-primary">{agentConfig.name}</span>!
                      </h3>
                      <p className="text-sm md:text-base text-muted-foreground">
                        {t('adLanding.test.ready')}
                      </p>
                    </div>
                  </div>
                </Card>

                <TestOptions agentConfig={agentConfig} sessionId={sessionId} agentId={agentId} onRegister={(sid, aid) => {
                setSessionId(sid);
                setAgentId(aid);
                setFlowStep('register');
              }} onBack={() => setFlowStep('chat')} />
              </div>}

            {flowStep === 'register' && <RegistrationPrompt agentId={agentId} agentConfig={agentConfig} sessionId={sessionId} onComplete={() => {
              window.location.href = 'https://manage.call24x7.ai/signup';
            }} />}
          </div>
        </section>

        {/* Social Proof - Minimal */}
        {flowStep === 'chat' && <section className="px-2 md:px-4 pb-6 md:pb-8">
            <div className="w-full max-w-4xl mx-auto">
              <div className={`flex flex-wrap justify-center items-center gap-4 md:gap-6 opacity-70 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center gap-1.5 md:gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Phone className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                  <span className="text-xs md:text-sm">{t('adLanding.social.calls')}</span>
                </div>
                <div className={`flex items-center gap-1.5 md:gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                  <span className="text-xs md:text-sm">{t('adLanding.social.businesses')}</span>
                </div>
                <div className={`flex items-center gap-1.5 md:gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Play className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                  <span className="text-xs md:text-sm">{t('adLanding.social.setup')}</span>
                </div>
              </div>
            </div>
          </section>}

        {/* Share Section - Eye-catching */}
        <section className="px-3 md:px-4 py-8 md:py-12">
          <div className="w-full max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 p-6 md:p-8 border border-primary/30">
              {/* Animated background glow */}
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10 animate-pulse" />
              <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-secondary/20 rounded-full blur-3xl" />
              
              <div className="relative z-10 text-center space-y-4">
                <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary/20 mb-2">
                  <Share2 className="w-7 h-7 md:w-8 md:h-8 text-primary" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  {t('share.spreadTheWord', 'Spread the Word!')}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground max-w-md mx-auto">
                  {t('share.helpOthers', 'Help other businesses discover the power of AI phone agents')}
                </p>
                <SocialShareButtons className="pt-2" />
              </div>
            </div>
          </div>
        </section>

        </main>

        {/* Footer - Minimal */}
        <footer className="border-t border-border/50 py-3 md:py-4 mt-auto">
          <div className="w-full px-3 md:px-4">
            <div className={`flex flex-col md:flex-row justify-between items-center gap-2 text-xs md:text-sm text-muted-foreground ${isRTL ? 'md:flex-row-reverse' : ''}`}>
              <span>© 2024 Call24x7.AI</span>
              <div className={`flex gap-3 md:gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <a href="/privacy-policy" className="hover:text-primary transition-colors">{t('adLanding.footer.privacy')}</a>
                <a href="/#pricing" className="hover:text-primary transition-colors">{t('adLanding.footer.pricing')}</a>
                <a href="/#api" className="hover:text-primary transition-colors">API</a>
                <a href="/#api" className="hover:text-primary transition-colors">Docs</a>
                <a href="/" className="hover:text-primary transition-colors">{t('adLanding.footer.home')}</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
};
export default AdLanding;